import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A Renault FT-style light tank: the first tank with the modern layout — a
// short boxy hull between two track units whose big front idler wheels ride
// higher than the rear, a prominent round riveted turret with a stubby gun,
// and the characteristic rear tail skid for trench crossing. Olive green,
// faces +Z, fits a single tile.

const OLIVE = "#5f6b3c";
const GUNMETAL = "#4a4d52";
const TRACK = "#33342f";
const RUST = "#7a4a30";

function box(
  w: number, h: number, d: number, color: string,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation };
}

function cyl(
  r: number, h: number, seg: number, color: string,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.CylinderGeometry(r, r, h, seg), color, position, rotation };
}

// One track unit: a large raised front idler, a smaller rear sprocket, the
// flat ground run and the sloped top run between the two wheel tops.
function trackUnit(x: number): Part[] {
  const frontR = 0.13;
  const rearR = 0.085;
  const frontZ = 0.24;
  const rearZ = -0.24;
  // top run endpoints: just over each wheel
  const y0 = frontR * 2 - 0.02; // above the front idler
  const y1 = rearR * 2 - 0.015; // above the rear sprocket
  const dz = rearZ - frontZ;
  const dy = y1 - y0;
  return [
    // front idler (big, raised look), axle along x
    cyl(frontR, 0.11, 12, TRACK, [x, frontR, frontZ], [0, 0, Math.PI / 2]),
    cyl(0.045, 0.12, 8, GUNMETAL, [x, frontR, frontZ], [0, 0, Math.PI / 2]),
    // rear sprocket
    cyl(rearR, 0.11, 10, TRACK, [x, rearR, rearZ], [0, 0, Math.PI / 2]),
    cyl(0.035, 0.12, 8, GUNMETAL, [x, rearR, rearZ], [0, 0, Math.PI / 2]),
    // ground run
    box(0.10, 0.05, 0.44, TRACK, [x, 0.045, 0]),
    // sloped top run from the front idler down to the rear sprocket
    box(0.10, 0.045, Math.hypot(dz, dy) + 0.04, TRACK,
      [x, (y0 + y1) / 2, (frontZ + rearZ) / 2], [Math.atan2(-dy, dz), 0, 0]),
  ];
}

function parts(): Part[] {
  const out: Part[] = [
    ...trackUnit(0.17),
    ...trackUnit(-0.17),

    // narrow hull between the tracks
    box(0.24, 0.18, 0.52, OLIVE, [0, 0.235, -0.03]),
    // angled front glacis with the driver's plate
    box(0.24, 0.04, 0.20, OLIVE, [0, 0.29, 0.25], [0.75, 0, 0]),
    box(0.20, 0.09, 0.03, OLIVE, [0, 0.30, 0.185], [0.25, 0, 0]),
    // sloping rear engine deck
    box(0.24, 0.04, 0.18, OLIVE, [0, 0.27, -0.30], [-0.55, 0, 0]),

    // the prominent round turret
    cyl(0.13, 0.15, 12, OLIVE, [0, 0.40, 0.02]),
    // domed cap with vision cupola
    cyl(0.10, 0.05, 12, OLIVE, [0, 0.50, 0.02]),
    cyl(0.045, 0.045, 8, GUNMETAL, [0, 0.545, 0.02]),
    // stubby gun with mantlet
    box(0.07, 0.07, 0.05, GUNMETAL, [0, 0.42, 0.15]),
    cyl(0.02, 0.15, 6, GUNMETAL, [0, 0.42, 0.235], [Math.PI / 2, 0, 0]),

    // rear tail skid: two rails running down and back, tied by a shoe plate
    box(0.03, 0.03, 0.24, RUST, [0.07, 0.115, -0.36], [-0.6, 0, 0]),
    box(0.03, 0.03, 0.24, RUST, [-0.07, 0.115, -0.36], [-0.6, 0, 0]),
    box(0.20, 0.025, 0.10, RUST, [0, 0.05, -0.42]),
  ];

  // rivet studs around the turret ring
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 3 + (i * (2 * Math.PI / 3)) / 4;
    out.push(box(0.02, 0.02, 0.02, GUNMETAL,
      [Math.sin(a) * 0.13, 0.365, 0.02 + Math.cos(a) * 0.13]));
  }

  return out;
}

export const lightTank = defineModel({
  id: "light-tank",
  name: "Light Tank",
  tags: ["vehicle", "wwi", "military", "tank", "armour", "low-poly"],
  batchId: "wwi",
  summary: "A Renault FT-style light tank with a round riveted turret.",
  description:
    "A Great War light tank in the Renault FT layout: short boxy olive hull " +
    "between track units with big raised front idlers, a prominent round " +
    "riveted turret with a stubby gun, and the rear tail skid for trench " +
    "crossing. Faces +Z, fits one tile.",
  author: "Fable 5",
  footprint: 0.5,
  tiles: { w: 1, h: 1 },
  parts,
  animations: [],
});
